import React from "react";
import { toast, Toaster } from "sonner";
import { useT } from "@/i18n";
import { useLiveScopeKey } from "@/live/live-provider";
import { useRouter } from "@/router";
import { currentTheme } from "@/theme";
import { NotificationPolicy, TOAST_STACK_CAP } from "./toast-queue";

const Context = React.createContext<NotificationPolicy | null>(null);
export function useNotifications() {
  const policy = React.useContext(Context);
  if (!policy) throw new Error("NotificationProvider missing");
  return policy;
}

function subscribeTheme(change: () => void) {
  const observer = new MutationObserver(change);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  return () => observer.disconnect();
}

function deliveryElement(target: EventTarget | null) {
  return target instanceof Element ? target.closest<HTMLElement>("[data-sonner-toast]") : null;
}

function lockDelivery(element: HTMLElement | undefined, hidden = false) {
  if (!element) return;
  element.dataset.state = "closing";
  element.inert = true;
  element.setAttribute("aria-disabled", "true");
  element.querySelectorAll("button").forEach((button) => { button.disabled = true; });
  // A late publication from a disposed scope must never paint old content or controls.
  if (hidden) element.style.display = "none";
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const scope = useLiveScopeKey();
  const { view } = useRouter();
  const t = useT();
  const theme = React.useSyncExternalStore(subscribeTheme, currentTheme);
  const focusedId = React.useRef<string | null>(null);
  const returnFocus = React.useRef<HTMLElement | null>(null);
  // Exiting native cards still consume Sonner visibility indices. Retain them until unmount so
  // they cannot hide a protected active card, even though policy admits only three active cards.
  const deliveries = React.useMemo(() => {
    const ids = new Set<string>();
    const elements = new Map<string, HTMLElement>();
    const listeners = new Set<() => void>();
    const change = () => listeners.forEach((listener) => listener());
    return {
      add(id: string) { ids.add(id); change(); },
      mount(id: string, element: HTMLElement) { elements.set(id, element); },
      lock(id: string) { lockDelivery(elements.get(id)); },
      remove(id: string) { ids.delete(id); elements.delete(id); change(); },
      count: () => Math.max(TOAST_STACK_CAP, ids.size),
      subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    };
  }, []);
  const visible = React.useSyncExternalStore(deliveries.subscribe, deliveries.count);
  const policy = React.useMemo(() => {
    const controller = new NotificationPolicy({
      show(id) {
        const spec = controller.get(id);
        if (!spec) return;
        deliveries.add(id);
        const primary = spec.open ? "open" : spec.undo ? "undo" : null;
        const notify = spec.tone === "danger" ? toast.error : toast[spec.tone ?? "info"];
        notify(<NotificationLifecycle id={id} policy={controller} deliveries={deliveries} title={spec.title} />, {
          id, testId: "notification", duration: Infinity, description: spec.msg,
          action: primary ? {
            label: spec[primary]!.label,
            onClick: (event) => { event.preventDefault(); controller.act(id, primary); },
          } : undefined,
          cancel: spec.open && spec.undo ? {
            label: spec.undo.label, onClick: () => controller.act(id, "undo"),
          } : undefined,
          onDismiss: () => controller.dismiss(id),
        });
      },
      dismiss: (id) => {
        // Native controls stay mounted during exit. Lock them synchronously before any callback
        // can run, then let Sonner own removal and animation.
        deliveries.lock(id);
        if (focusedId.current === id) {
          const target = returnFocus.current;
          focusedId.current = null;
          returnFocus.current = null;
          if (target?.isConnected && !target.closest("[inert]") && !target.matches(":disabled")) {
            target.focus({ preventScroll: true });
          }
        }
        toast.dismiss(id);
      },
    });
    return controller;
  }, [scope, deliveries]);
  React.useLayoutEffect(() => () => {
    focusedId.current = null;
    returnFocus.current = null;
    policy.dispose();
  }, [policy]);
  return <Context.Provider value={policy}>
    {children}
    <div data-notification-host="true"
      onClickCapture={(event) => {
        const item = deliveryElement(event.target);
        const id = item?.dataset.deliveryId;
        if (!id) return;
        const spec = policy.get(id);
        if (!spec) { event.preventDefault(); event.stopPropagation(); return; }
        if (!(event.target as Element).closest("button") && spec.open) {
          event.preventDefault();
          event.stopPropagation();
          policy.act(id, "open");
        }
      }}
      onPointerOver={(event) => {
        const item = deliveryElement(event.target);
        if (event.relatedTarget instanceof Node && item?.contains(event.relatedTarget)) return;
        if (item?.dataset.deliveryId) policy.interact(item.dataset.deliveryId, "hover", true);
      }}
      onPointerOut={(event) => {
        const item = deliveryElement(event.target);
        if (event.relatedTarget instanceof Node && item?.contains(event.relatedTarget)) return;
        if (item?.dataset.deliveryId) policy.interact(item.dataset.deliveryId, "hover", false);
      }}
      onFocusCapture={(event) => {
        // Own focus return because the policy locks controls before Sonner unmounts them.
        event.stopPropagation();
        if (event.relatedTarget instanceof HTMLElement && !event.currentTarget.contains(event.relatedTarget)) {
          returnFocus.current = event.relatedTarget;
        }
        const id = deliveryElement(event.target)?.dataset.deliveryId;
        focusedId.current = id ?? null;
        if (id) policy.interact(id, "focus", true);
      }}
      onBlurCapture={(event) => {
        event.stopPropagation();
        if (event.relatedTarget instanceof HTMLElement && !event.currentTarget.contains(event.relatedTarget)) {
          focusedId.current = null;
          returnFocus.current = null;
        }
        const item = deliveryElement(event.target);
        if (event.relatedTarget instanceof Node && item?.contains(event.relatedTarget)) return;
        if (item?.dataset.deliveryId) policy.interact(item.dataset.deliveryId, "focus", false);
      }}>
      <Toaster position="bottom-right" expand closeButton richColors theme={theme} visibleToasts={visible} swipeDirections={[]}
        gap={8} style={{ "--width": "344px" } as React.CSSProperties}
        className={view.name === "mail" ? "notification-stack notification-stack--clear-actions" : "notification-stack"}
        customAriaLabel={t("toast.region")} containerAriaLabel={t("toast.region")}
        toastOptions={{ closeButtonAriaLabel: t("common.close"), style: { padding: 10 } }}
      />
    </div>
  </Context.Provider>;
}

/** A lifecycle marker inside Sonner's native title; Sonner owns every visual element. */
function NotificationLifecycle({ id, policy, deliveries, title }: {
  id: string; policy: NotificationPolicy; title?: string;
  deliveries: { mount: (id: string, element: HTMLElement) => void; remove: (id: string) => void };
}) {
  const marker = React.useRef<HTMLSpanElement>(null);
  const active = React.useSyncExternalStore(policy.subscribe, () => Boolean(policy.get(id)));
  React.useLayoutEffect(() => {
    const item = deliveryElement(marker.current);
    if (item) {
      item.dataset.deliveryId = id;
      item.dataset.state = "open";
      deliveries.mount(id, item);
    }
    return () => deliveries.remove(id);
  }, [id, deliveries]);
  React.useLayoutEffect(() => {
    if (active) return;
    lockDelivery(deliveryElement(marker.current) ?? undefined, policy.isDisposed());
    toast.dismiss(id);
  }, [active, id, policy]);
  return <span ref={marker}>{title}</span>;
}
